/**
 * Data container for the units table rendered inside an expanded FlexiblePropertyCard. Fetches the
 * listing's units, normalizes them, and hands them to the shared UnitsTableView.
 */
import { useFlexiblePropertiesSearch } from '../../../contexts/FlexiblePropertiesSearchContext';
import { useGetPropertyTableUnits } from '../../../hooks/queries/propertyTable';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import UnitsTableView, { UNIT_TABLE_VARIANT } from './UnitsTableView';

import type { AccountViewListingGroupDto, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { UnitTableRow } from './UnitsTableView';

type Props = {
  listing: AccountViewListingGroupDto;
  onArchiveClick?: (listingAlias: string, address: RentalManagerAddressDto) => void;
  onEditClick?: (listingAlias: string) => void;
};

const UnitsTable = ({ listing, onArchiveClick, onEditClick }: Props) => {
  const groupId = listing.groupId;
  const { searchText, includeArchived } = useFlexiblePropertiesSearch();

  const {
    dataByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingByGroupId,
    isFetchingNextPageByGroupId,
  } = useGetPropertyTableUnits(
    {
      searchText,
      includeArchived,
      includeUnarchived: !includeArchived,
      limit: PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT,
    },
    [groupId],
  );

  const unitsData = dataByGroupId[groupId];
  const units = unitsData?.units ?? [];

  const rows: Array<UnitTableRow> = units.map((unit) => ({
    listingAlias: unit.listingAlias,
    address: listing.address,
    photoKey: unit.photoKey,
    unitLabel: unit.unitNumber ? `Unit ${unit.unitNumber}` : '—',
    unitAccessibleLabel: unit.unitNumber || 'Unit',
    unitInfo: `${unit.bedroomCount ?? '—'} bd • ${unit.bathroomCount ?? '—'} ba • ${unit.squareFeet ?? '—'} sqft`,
    overflowLabel: unit.unitNumber,
    rent: unit.rent,
    isTotalMonthlyPrice: unit.isTotalMonthlyPrice,
    isMultifamily: unit.isMultifamily,
  }));

  const handleLoadMore = () => {
    const endKey = unitsData?.pager?.endKey;
    if (endKey) {
      fetchNextPageForGroup(groupId, endKey);
    }
  };

  return (
    <UnitsTableView
      variant={UNIT_TABLE_VARIANT.CARD}
      rows={rows}
      isLoading={(isFetchingByGroupId[groupId] ?? false) && units.length === 0}
      hasNextPage={hasNextPageByGroupId[groupId] ?? false}
      isFetchingNextPage={isFetchingNextPageByGroupId[groupId] ?? false}
      onLoadMore={handleLoadMore}
      onArchive={onArchiveClick}
      onEdit={onEditClick}
      addRentGroupId={groupId}
    />
  );
};

export default UnitsTable;
